export interface IncomeFormOtherSourcesKey {
    key: string;
    title: string;
    type: 'amount' | 'text';
    order: number;
    scope?: 'detail' | 'parent';
}

export interface OtherSourcesTableColumn {
    key: string;
    title: string;
    isAmount?: boolean;
    isComputedNet?: boolean;
}

export const IFOS_TYPE_KEY = 'ifosTypeName';
export const IFOS_DETAILS_KEY = 'ifosDetailDtos';
export const SRO_TYPE = 'INCOME_EXEMPTED_REDUCED_BY_SRO';

export const IFOS_TYPE_LABELS: Record<string, string> = {
    interest_profit_bank_fi: 'Interest/Profit (Bank & FI)',
    dividend_from_listed_company: 'Dividend (From Listed Company)',
    dividend_others: 'Dividend (Other than Listed Company & Mutual/Unit Fund)',
    income_from_mutual_unit_fund: 'Income from Mutual/Unit Fund',
    CAL_IFOS_ROYALTIES: 'Royalties',
    CAL_IFOS_LICENSE_FEE: 'License Fee',
    CAL_IFOS_TECH_ASSIST_FEE: 'Fees for Technical Services',
    CAL_IFOS_INTANGIBLE_ASSETS: 'Income From Intangible Assets',
    CAL_EXPORT_CASH_SUBSIDY: 'Cash Subsidy',
    consultancy_service: 'Consultancy Service',
    CAL_IFOS_WPPF: 'Payment from WPPF',
    CAL_LOTTERY_WINNINGS: 'Lottery, Puzzle, Card Game/Online Game, or Similar',
    OTHER_SOURCES_WITH_TDS: 'Meeting Fee, Honorarium etc. (with TDS)',
    ANY_OTHER_INCOME: 'Any Other Income',
    INCOME_EXEMPTED_REDUCED_BY_SRO: 'Income Subject to Reduced Tax Rate',
    JOINT_VENTURE_PROFIT_SHARE: 'Joint Venture(JV) Profit Share',
};

export const INCOME_FORM_OTHER_SOURCES_SRO_KEYS: IncomeFormOtherSourcesKey[] = [
    { key: 'name', title: 'Particulars', type: 'text', order: 1 },
    { key: 'paymentAuthority', title: 'Payment Authority', type: 'text', order: 2 },
    { key: 'accAgRefNo', title: 'Reference No', type: 'text', order: 3 },
    { key: 'dateOfIssue', title: 'Date', type: 'text', order: 4 },
    { key: 'gross', title: 'Gross Amount Received', type: 'amount', order: 5 },
    { key: 'expense', title: 'Related Expenses', type: 'amount', order: 6 },
    { key: 'sroNo', title: 'SRO No', type: 'text', order: 7 },
    { key: 'year', title: 'Year', type: 'text', order: 8 },
    { key: 'particularSro', title: 'Particular of SRO', type: 'text', order: 9 },
    { key: 'taxableIncome', title: 'Taxable Income', type: 'amount', order: 10 },
    { key: 'taxable', title: 'Net Income', type: 'amount', order: 11, scope: 'parent' },
    { key: 'exempted', title: 'Income Exempted from Tax', type: 'amount', order: 12, scope: 'parent' },
    { key: 'applicableTaxSRO', title: 'Applicable Tax as per SRO', type: 'amount', order: 13, scope: 'parent' },
];

export const IFOS_TABLE_COLUMNS: Record<string, OtherSourcesTableColumn[]> = {
    CAL_IFOS_ROYALTIES: [
        { key: 'name', title: 'Name of the Client' },
        { key: 'accAgRefNo', title: 'Agreement No' },
        { key: 'dateOfIssue', title: 'Date' },
        { key: 'gross', title: 'Gross Payment Amount', isAmount: true },
        { key: 'expense', title: 'Related Expenses', isAmount: true },
        { key: 'netIncome', title: 'Net Income', isAmount: true, isComputedNet: true },
        { key: 'deductedTax', title: 'Tax Deducted/Collected at Source', isAmount: true },
    ],
    consultancy_service: [
        { key: 'name', title: 'Name of the Client' },
        { key: 'accAgRefNo', title: 'Agreement No' },
        { key: 'dateOfIssue', title: 'Date' },
        { key: 'gross', title: 'Gross Payment Amount', isAmount: true },
        { key: 'expense', title: 'Related Expenses', isAmount: true },
        { key: 'netIncome', title: 'Net Income', isAmount: true, isComputedNet: true },
        { key: 'deductedTax', title: 'Tax Deducted/Collected at Source', isAmount: true },
    ],
    CAL_IFOS_WPPF: [
        { key: 'name', title: 'Name of the Company' },
        { key: 'dateOfIssue', title: 'Date of Payment' },
        { key: 'accAgRefNo', title: 'Reference No.' },
        { key: 'gross', title: 'Gross Amount', isAmount: true },
        { key: 'deductedTax', title: 'Tax Deducted/Collected at Source', isAmount: true },
    ],
    CAL_LOTTERY_WINNINGS: [
        { key: 'name', title: 'Type of Winning' },
        { key: 'paymentAuthority', title: 'Payment Authority' },
        { key: 'accAgRefNo', title: 'Reference No' },
        { key: 'gross', title: 'Gross Winning Amount', isAmount: true },
        { key: 'deductedTax', title: 'Tax Deducted/Collected at Source', isAmount: true },
    ],
    CAL_IFOS_LICENSE_FEE: [
        { key: 'name', title: 'License Name/Details' },
        { key: 'licenseType', title: 'License Type' },
        { key: 'dateOfIssue', title: 'Date' },
        { key: 'gross', title: 'Gross Amount Received', isAmount: true },
        { key: 'expense', title: 'Related Expense', isAmount: true },
        { key: 'netIncome', title: 'Net Income', isAmount: true, isComputedNet: true },
        { key: 'deductedTax', title: 'TDS', isAmount: true },
    ],
    CAL_IFOS_TECH_ASSIST_FEE: [
        { key: 'name', title: 'Particulars' },
        { key: 'paymentAuthority', title: 'Payment Authority' },
        { key: 'dateOfIssue', title: 'Date' },
        { key: 'gross', title: 'Gross Amount Received', isAmount: true },
        { key: 'expense', title: 'Related Expense', isAmount: true },
        { key: 'netIncome', title: 'Net Income', isAmount: true, isComputedNet: true },
        { key: 'deductedTax', title: 'TDS', isAmount: true },
    ],
    CAL_IFOS_INTANGIBLE_ASSETS: [
        { key: 'name', title: 'Particulars' },
        { key: 'paymentAuthority', title: 'Payment Authority' },
        { key: 'dateOfIssue', title: 'Date' },
        { key: 'gross', title: 'Gross Amount Received', isAmount: true },
        { key: 'expense', title: 'Related Expense', isAmount: true },
        { key: 'netIncome', title: 'Net Income', isAmount: true, isComputedNet: true },
        { key: 'deductedTax', title: 'TDS', isAmount: true },
    ],
    CAL_EXPORT_CASH_SUBSIDY: [
        { key: 'name', title: 'Amount Details' },
        { key: 'paymentAuthority', title: 'Payment Authority' },
        { key: 'dateOfIssue', title: 'Date' },
        { key: 'gross', title: 'Gross Amount Received', isAmount: true },
        { key: 'expense', title: 'Related Expense', isAmount: true },
        { key: 'netIncome', title: 'Net Income', isAmount: true, isComputedNet: true },
        { key: 'deductedTax', title: 'TDS', isAmount: true },
    ],
    OTHER_SOURCES_WITH_TDS: [
        { key: 'name', title: 'Particulars' },
        { key: 'paymentAuthority', title: 'Payment Authority' },
        { key: 'dateOfIssue', title: 'Date' },
        { key: 'gross', title: 'Gross Amount Received', isAmount: true },
        { key: 'expense', title: 'Related Expense', isAmount: true },
        { key: 'netIncome', title: 'Net Income', isAmount: true, isComputedNet: true },
        { key: 'deductedTax', title: 'Tax Deducted/Collected at Source', isAmount: true },
    ],
    ANY_OTHER_INCOME: [
        { key: 'name', title: 'Particulars' },
        { key: 'paymentAuthority', title: 'Payment Authority' },
        { key: 'dateOfIssue', title: 'Date' },
        { key: 'gross', title: 'Gross Amount Received', isAmount: true },
        { key: 'expense', title: 'Related Expense', isAmount: true },
        { key: 'netIncome', title: 'Net Income', isAmount: true, isComputedNet: true },
        { key: 'deductedTax', title: 'Tax Deducted/Collected at Source', isAmount: true },
    ],
    JOINT_VENTURE_PROFIT_SHARE: [
        { key: 'name', title: 'Particulars' },
        { key: 'paymentAuthority', title: 'Payment Authority' },
        { key: 'dateOfIssue', title: 'Date' },
        { key: 'gross', title: 'Gross Amount Received', isAmount: true },
        { key: 'expense', title: 'Related Expense', isAmount: true },
        { key: 'netIncome', title: 'Net Income', isAmount: true, isComputedNet: true },
        { key: 'deductedTax', title: 'Tax Deducted/Collected at Source', isAmount: true },
    ],
    interest_profit_bank_fi: [
        { key: 'name', title: 'Name of Bank/FI' },
        { key: 'accAgRefNo', title: 'Account No' },
        { key: 'gross', title: 'Gross Amount', isAmount: true },
        { key: 'expense', title: 'Related Fees', isAmount: true },
        { key: 'deductedTax', title: 'Source Tax Deducted', isAmount: true },
    ],
    dividend_from_listed_company: [
        { key: 'name', title: 'Name of the Company' },
        { key: 'gross', title: 'Gross Dividend Amount', isAmount: true },
        { key: 'expense', title: 'Related Expenses', isAmount: true },
        { key: 'deductedTax', title: 'Source Tax Deducted', isAmount: true },
    ],
    dividend_others: [
        { key: 'name', title: 'Name of the Company' },
        { key: 'gross', title: 'Gross Dividend Amount', isAmount: true },
        { key: 'expense', title: 'Related Expenses', isAmount: true },
        { key: 'deductedTax', title: 'Source Tax Deducted', isAmount: true },
    ],
    income_from_mutual_unit_fund: [
        { key: 'name', title: 'Name of Mutual/Unit Fund' },
        { key: 'gross', title: 'Gross Receipt', isAmount: true },
        { key: 'expense', title: 'Related Expenses', isAmount: true },
        { key: 'deductedTax', title: 'Source Tax Deducted', isAmount: true },
    ],
};

export const DEFAULT_IFOS_TABLE_COLUMNS: OtherSourcesTableColumn[] = [
    { key: 'name', title: 'Particulars' },
    { key: 'paymentAuthority', title: 'Payment Authority' },
    { key: 'dateOfIssue', title: 'Date' },
    { key: 'gross', title: 'Gross Amount Received', isAmount: true },
    { key: 'expense', title: 'Related Expense', isAmount: true },
    { key: 'netIncome', title: 'Net Income', isAmount: true, isComputedNet: true },
    { key: 'deductedTax', title: 'Tax Deducted/Collected at Source', isAmount: true },
];
